"use client";

import { useState } from "react";
import { LuCheck, LuChevronsUpDown, LuX } from "react-icons/lu";
import { cn } from "~/utils/fontsConfig";

import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInputV4,
    CommandItem,
} from "~/components/shadcn/Command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";
import { SelectCustomization } from "~/types/CustomizableLayout";
interface TagsInputProps {
    value: string[];
    onChange: (value: string[]) => void;
    suggestions?: string[];
    placeholder?: string;
    emptyMessage?: string;
    disabled?: boolean;
    className?: string;
    customization?: SelectCustomization;
}

export const TagsInput = ({
    value,
    onChange,
    suggestions = [],
    placeholder = "Select items...",
    emptyMessage = "No matches found.",
    disabled = false,
    className,
    customization,
}: TagsInputProps) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");

    const filteredSuggestions = [
        ...(inputValue &&
        !suggestions.includes(inputValue) &&
        !value.includes(inputValue)
            ? [inputValue]
            : []),
        ...suggestions.filter(
            (item) =>
                !value.includes(item) &&
                item.toLowerCase().includes(inputValue.toLowerCase()),
        ),
    ];

    const handleSelect = (selectedValue: string) => {
        if (!value.includes(selectedValue)) {
            onChange([...value, selectedValue]);
        }
        setOpen(false);
        setInputValue("");
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && inputValue) {
            e.preventDefault();
            if (!value.includes(inputValue)) {
                onChange([...value, inputValue]);
                setInputValue("");
            }
        }
    };

    const handleRemove = (tagToRemove: string) => {
        onChange(value.filter((tag) => tag !== tagToRemove));
    };

    return (
        <div className="flex flex-col gap-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <div
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                            "flex h-full min-h-10 w-full cursor-pointer justify-between bg-v3-N-500 p-2 transition-colors hover:bg-v3-N-500/90",
                            "border border-transparent hover:border-v3-clover focus:border-v3-clover focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                            "whitespace-normal rounded-[10px] text-left align-top",
                            open && "border border-v3-clover",
                            className,
                            customization?.inputClassName,
                        )}
                    >
                        <div className="flex flex-wrap gap-1.5 py-1.5">
                            {value.length > 0 ? (
                                value.map((tag) => (
                                    <div
                                        key={tag}
                                        className={cn(
                                            "flex items-center rounded-lg bg-v3-N-400 px-2 py-1 text-sm",
                                            customization?.itemClassName,
                                        )}
                                    >
                                        <span>{tag}</span>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleRemove(tag);
                                            }}
                                            className={cn(
                                                "ml-1 p-1 hover:text-v3-N-100",
                                                disabled && "hidden",
                                            )}
                                            aria-label={`Remove tag ${tag}`}
                                        >
                                            <LuX />
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <span className="text-sm text-v3-N-400">
                                    {placeholder}
                                </span>
                            )}
                        </div>
                        <LuChevronsUpDown className="ml-2 mt-2 h-4 w-4 shrink-0 self-start opacity-50" />
                    </div>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] border-none p-0">
                    <Command>
                        <CommandInputV4
                            placeholder="Search or type new tag..."
                            value={inputValue}
                            onValueChange={setInputValue}
                            onKeyDown={handleKeyDown}
                            wrapperClass={customization?.inputClassName}
                            className={customization?.className}
                        />
                        <CommandEmpty>
                            {inputValue
                                ? `Press Enter to add "${inputValue}"`
                                : emptyMessage}
                        </CommandEmpty>
                        <CommandGroup className={customization?.listClassName}>
                            {filteredSuggestions.map((item) => (
                                <CommandItem
                                    key={item}
                                    value={item}
                                    onSelect={handleSelect}
                                    className={customization?.itemClassName}
                                >
                                    {item}
                                    <LuCheck
                                        className={cn(
                                            "ml-auto h-4 w-4",
                                            value.includes(item)
                                                ? "opacity-100"
                                                : "opacity-0",
                                        )}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
};
